export const IDENTITY_REQUIREMENTS = ["none", "provider_attested", "nist_ial2"] as const

export type IdentityRequirement = (typeof IDENTITY_REQUIREMENTS)[number]

export const IDENTITY_REQUIREMENT_LABELS: Record<IdentityRequirement, string> = {
  none: "No ID verification",
  provider_attested: "Basic ID verification",
  nist_ial2: "NIST IAL2",
}

export const IDENTITY_PROVIDER_KEYS = ["proof", "persona", "stripe"] as const

export type IdentityProviderKey = (typeof IDENTITY_PROVIDER_KEYS)[number]

export const IDENTITY_PROVIDER_LABELS: Record<IdentityProviderKey, string> = {
  proof: "Proof",
  persona: "Persona",
  stripe: "Stripe",
}

export const IDENTITY_VERIFICATION_RESULTS = [
  "passed",
  "failed_retryable",
  "failed_terminal",
  "manual_verified",
  "manual_review",
  "unavailable",
] as const

export type IdentityVerificationResult = (typeof IDENTITY_VERIFICATION_RESULTS)[number]

export const IDENTITY_VERIFICATION_STATUSES = [
  "required_pending",
  "initiated",
  "passed",
  "failed_retryable",
  "failed_terminal",
  "manual_review",
  "unavailable",
  "expired",
] as const

export type IdentityVerificationStatus = (typeof IDENTITY_VERIFICATION_STATUSES)[number]

export type IdentityProviderRouting = {
  providerForProviderAttested?: IdentityProviderKey | null
  providerForNistIal2?: IdentityProviderKey | null
}

const IDENTITY_REQUIREMENT_RANK: Record<IdentityRequirement, number> = {
  none: 0,
  provider_attested: 1,
  nist_ial2: 2,
}

export function compareIdentityRequirements(left: IdentityRequirement, right: IdentityRequirement) {
  return IDENTITY_REQUIREMENT_RANK[left] - IDENTITY_REQUIREMENT_RANK[right]
}

export function maxIdentityRequirement(
  left: IdentityRequirement,
  right: IdentityRequirement,
): IdentityRequirement {
  return compareIdentityRequirements(left, right) >= 0 ? left : right
}

export function identityRequirementSatisfies(
  achieved: IdentityRequirement,
  required: IdentityRequirement,
) {
  return compareIdentityRequirements(achieved, required) >= 0
}

export function getProviderForIdentityRequirement(
  requirement: IdentityRequirement,
  routing: IdentityProviderRouting,
): IdentityProviderKey | null {
  if (requirement === "provider_attested") {
    return routing.providerForProviderAttested ?? null
  }

  if (requirement === "nist_ial2") {
    return routing.providerForNistIal2 ?? null
  }

  return null
}
